"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import {
  Briefcase,
  Calendar,
  Check,              
  ChevronDown,
  FileText,
  Mail,
  Info,
} from "lucide-react";

import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageProvider";
import { ambilTeks, kodeBahasa } from "../data/pengumuman";
import { LOWONGAN, KONTAK_HRD, masihDibuka, tautanLamar } from "./data";

function BerkasLamaran({ berkas, kode }) {
  const data =
    berkas && typeof berkas === "object" && berkas[kode]
      ? berkas[kode]
      : null;

  // Mendukung data lowongan dengan format lama.
  if (!data || typeof data === "string") {
    const teks =
      typeof berkas === "string"
        ? berkas
        : berkas?.[kode];

    return (
      <div className="mb-6 flex gap-3 rounded-2xl bg-[#0f6a3b]/[0.06] p-4">
        <FileText
          className="mt-0.5 h-4 w-4 shrink-0 text-[#0f6a3b]"
          aria-hidden="true"
        />
        <p className="text-sm leading-relaxed text-[var(--c-body)]">
          {teks}
        </p>
      </div>
    );
  }

  return (
    <section className="mb-6 rounded-2xl border border-[#0f6a3b]/10 bg-[#0f6a3b]/[0.05] p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0f6a3b]/10">
          <FileText
            className="h-5 w-5 text-[#0f6a3b]"
            aria-hidden="true"
          />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-[var(--c-ink)]">
            {data.judul}
          </h3>

          <p className="mt-1 text-sm leading-6 text-[var(--c-body)]">
            {data.deskripsi}
          </p>

          <p className="mt-2 text-sm leading-6 text-[var(--c-body)]">
            {data.dokumen}
          </p>
        </div>
      </div>

      <div className="mt-4 border-t border-[#0f6a3b]/10 pt-4">
        <p className="text-sm text-[var(--c-body)]">
          {data.labelEmail}
        </p>

        <a
          href={`mailto:${data.email || ""}`}
          className="mt-1 inline-block break-all font-semibold text-[#0f6a3b] hover:underline"
        >
          {data.email}
        </a>
      </div>

      <div className="mt-4 flex items-start gap-2 border-t border-[#0f6a3b]/10 pt-4">
        <Info
          className="mt-1 h-4 w-4 shrink-0 text-[#0f6a3b]"
          aria-hidden="true"
        />

        <p className="text-sm leading-6 text-[var(--c-body)]">
          {data.catatan}
        </p>
      </div>
    </section>
  );
}

export default function KarirContent() {
  const ctx = useLanguage();
  const kode = kodeBahasa(ctx);

  /* Mulai dari SEMUA lowongan, disaring setelah menempel.

     Halaman ini di-render statis saat build. Kalau disaring saat
     render, "hari ini" versi peladen adalah tanggal build - bisa
     berminggu-minggu lalu - dan hasilnya berbeda dari saringan di
     browser: hydration error. Menyaring setelah menempel memakai
     tanggal pengunjung yang sebenarnya. Lowongan yang sudah tutup
     sempat terlihat sekejap; itu harga yang wajar. */
  const [daftar, setDaftar] = useState(LOWONGAN);
  useEffect(() => {
    const sekarang = new Date();
    setDaftar(LOWONGAN.filter((l) => masihDibuka(l, sekarang)));
  }, []);

  /* Kartu mana yang kualifikasinya sedang dibuka, dikunci per-slug.
     Default tertutup: kualifikasi muncul saat "Lihat selengkapnya"
     diklik. Memakai objek, bukan satu boolean, supaya tiap kartu
     buka-tutup sendiri. */
  const [terbuka, setTerbuka] = useState({});
  const toggle = (slug) => setTerbuka((s) => ({ ...s, [slug]: !s[slug] }));

  const formatTanggal = (iso) =>
    new Date(iso).toLocaleDateString(kode === "en" ? "en-GB" : "id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    });

  /* Pemisah dua bagian. Pemicunya POSTER, bukan kualifikasi teks:
     sebuah posisi dianggap benar-benar dibuka begitu posternya ada -
     syarat lengkapnya tercetak di poster itu. Entri yang belum punya
     poster (perawat, kasir) tetap "Segera dibuka". Ini membuat posisi
     berposter tapi tanpa teks kualifikasi (mis. Admin Rawat Jalan)
     tetap tampil lengkap dengan gambarnya.

     Konsekuensi: kartu tanpa teks kualifikasi tidak menampilkan daftar
     "Kualifikasi" maupun tombol "Lihat selengkapnya" (tidak ada yang
     dibuka) - syaratnya dibaca langsung dari poster. Untuk JobPosting/
     SEO, teks kualifikasi tetap sebaiknya diisi di data.js karena
     Google membaca teks, bukan gambar. */
  const dibuka = daftar.filter((l) => l.poster?.src);
  const disiapkan = daftar.filter((l) => !l.poster?.src);

  return (
    <div className="relative w-full overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-16 lg:px-10">

        {/* ---------- KEPALA HALAMAN ---------- */}
        <Reveal>
          <header className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0f6a3b] sm:text-sm">
              {ambilTeks({ id: "JMC Karir", en: "JMC Careers" }, kode)}
            </p>
            <h1 className="text-3xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-4xl">
              {ambilTeks(
                { id: "Bergabung bersama RS Jakarta Medical Center", en: "Join Jakarta Medical Center Hospital" },
                kode
              )}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-[var(--c-body)] sm:text-base">
              {ambilTeks(
                {
                  id: "Berikut posisi yang sedang kami buka. Lamaran dikirim melalui email dengan melampirkan berkas yang diminta pada masing-masing posisi.",
                  en: "These are the positions we are currently hiring for. Apply by email, attaching the documents listed for each position.",
                },
                kode
              )}
            </p>
          </header>
        </Reveal>

        {/* ---------- DAFTAR LOWONGAN ---------- */}
        {daftar.length === 0 ? (
          /* Keadaan kosong ditulis sungguhan, bukan dibiarkan jadi
             halaman kosong. Begitu semua `ditutup` lewat, inilah yang
             dilihat pengunjung - dan "belum ada lowongan" jauh lebih
             baik daripada halaman yang terlihat rusak. */
          <div className="mx-auto max-w-xl rounded-2xl border border-[var(--c-line)] bg-white p-8 text-center dark:bg-white/5">
            <Briefcase className="mx-auto mb-3 h-8 w-8 text-[#0f6a3b]" aria-hidden="true" />
            <p className="text-base text-[var(--c-body)]">
              {ambilTeks(
                {
                  id: "Saat ini belum ada lowongan yang dibuka. Silakan kembali lagi nanti.",
                  en: "There are no open positions right now. Please check back later.",
                },
                kode
              )}
            </p>
          </div>
        ) : (
          <>
            {/* ---------- SEDANG DIBUKA: kartu lengkap + poster, 2 kolom ----------
                Dua kolom mulai lg. Di bawah itu satu kolom: poster persegi
                di kartu selebar setengah tablet terlalu kecil untuk dikenali.
                items-start: tiap kartu setinggi isinya sendiri, jadi membuka
                satu kartu tidak menarik tetangganya ikut memanjang. */}
            {dibuka.length > 0 && (
              <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-10">
                {dibuka.map((l, i) => {
                  const buka = !!terbuka[l.slug];
                  const adaKualifikasi = l.kualifikasi.length > 0;
                  return (
                    <Reveal key={l.slug} delay={i * 80}>
                      {/* id = slug, supaya HRD bisa membagikan tautan langsung
                          ke satu posisi: rsjmc.id/karir#apoteker */}
                      <article
                        id={l.slug}
                        className="flex h-full scroll-mt-32 flex-col overflow-hidden rounded-[28px] border border-[var(--c-line)] bg-white dark:bg-white/5"
                      >
                        {/* object-contain supaya poster rasio apa pun (mis.
                            driver yang potret) tampil utuh, tidak terpotong. */}
                        <Image
                          src={l.poster.src}
                          width={l.poster.lebar}
                          height={l.poster.tinggi}
                          alt={ambilTeks(l.poster.alt, kode)}
                          sizes="(min-width: 1024px) 420px, 100vw"
                          className="w-full h-auto bg-[#eaf2fb] object-contain dark:bg-white/5"
                        />

                        <div className="flex flex-1 flex-col p-6 sm:p-8">
                          <h2 className="mb-1 text-2xl font-bold tracking-tight text-[var(--c-ink)]">
                            {ambilTeks(l.posisi, kode)}
                          </h2>

                          {/* Batas waktu hanya ditulis kalau ada. Menulis
                              "Dibuka sampai: -" lebih membingungkan daripada
                              tidak menulis apa pun. */}
                          {l.ditutup && (
                            <p className="mb-4 flex items-center gap-1.5 text-sm text-[var(--c-body)]">
                              <Calendar className="h-4 w-4" aria-hidden="true" />
                              {ambilTeks({ id: "Dibuka sampai", en: "Open until" }, kode)}{" "}
                              <time dateTime={l.ditutup} className="font-semibold text-[var(--c-ink)]">
                                {formatTanggal(l.ditutup)}
                              </time>
                            </p>
                          )}

                          {adaKualifikasi ? (
                            /* Ada teks kualifikasi: sembunyikan di balik toggle.
                               Blok tetap di DOM (atribut `hidden`) supaya terbaca
                               mesin pencari & pembaca layar walau tertutup. */
                            <>
                              <button
                                type="button"
                                onClick={() => toggle(l.slug)}
                                aria-expanded={buka}
                                aria-controls={`kualifikasi-${l.slug}`}
                                className="mb-5 mt-4 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-[#0f6a3b] transition-colors hover:text-[#0c5730] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6a3b]"
                              >
                                {ambilTeks(
                                  buka
                                    ? { id: "Tutup", en: "Show less" }
                                    : { id: "Lihat selengkapnya", en: "See more" },
                                  kode
                                )}
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform ${buka ? "rotate-180" : ""}`}
                                  aria-hidden="true"
                                />
                              </button>

                              <div id={`kualifikasi-${l.slug}`} hidden={!buka}>
                                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#0f6a3b]">
                                  {ambilTeks({ id: "Kualifikasi", en: "Qualifications" }, kode)}
                                </h3>
                                <ul className="mb-6 grid gap-2.5">
                                  {l.kualifikasi.map((k, j) => (
                                    <li key={j} className="flex items-start gap-2.5">
                                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0f6a3b]" aria-hidden="true" />
                                      <span className="text-sm leading-snug text-[var(--c-body)] sm:text-[15px]">
                                        {ambilTeks(k, kode)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>

                                <BerkasLamaran berkas={l.berkas} kode={kode} />
                              </div>
                            </>
                          ) : (
                            /* Tanpa teks kualifikasi (mis. Admin Rawat Jalan):
                               syaratnya dibaca dari poster. Tak ada toggle kosong;
                               kotak berkas ditampilkan langsung. */
                            <div className="mt-4">
                              <BerkasLamaran berkas={l.berkas} kode={kode} />
                            </div>
                          )}

                          {/* mt-auto mendorong isi ke dasar kartu. Tombol Lamar
                              selalu tampil. */}
                          <div className="mt-auto">
                            <a
                              href={tautanLamar(l)}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0f6a3b] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0c5730] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6a3b] sm:w-auto sm:text-base"
                            >
                              <Mail className="h-4 w-4" aria-hidden="true" />
                              {ambilTeks({ id: "Lamar via email", en: "Apply by email" }, kode)}
                            </a>

                            <p className="mt-3 text-sm text-[var(--c-body)]">
                              {ambilTeks({ id: "atau kirim ke", en: "or send to" }, kode)}{" "}
                              <span className="select-all font-semibold text-[var(--c-ink)]">
                                {KONTAK_HRD.email}
                              </span>
                            </p>
                          </div>
                        </div>
                      </article>
                    </Reveal>
                  );
                })}
              </div>
            )}

            {/* ---------- SEGERA DIBUKA: kartu ringkas, nama + badge ----------
                Posisi yang BELUM punya poster. Begitu sebuah entri diberi
                `poster` di data.js, entri itu otomatis pindah ke bagian
                "Sedang dibuka" di atas - tanpa mengubah berkas ini. */}
            {disiapkan.length > 0 && (
              <section className="mt-14 md:mt-20">
                <Reveal>
                  <div className="mx-auto mb-6 max-w-3xl text-center md:mb-8">
                    <h2 className="text-xl font-bold tracking-tight text-[var(--c-ink)] sm:text-2xl">
                      {ambilTeks({ id: "Segera dibuka", en: "Opening soon" }, kode)}
                    </h2>
                    <p className="mt-2 text-sm text-[var(--c-body)]">
                      {ambilTeks(
                        {
                          id: "Posisi berikut sedang kami siapkan. Detail kualifikasi dan cara melamar menyusul.",
                          en: "These positions are being prepared. Qualifications and how to apply will follow.",
                        },
                        kode
                      )}
                    </p>
                  </div>
                </Reveal>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {disiapkan.map((l, i) => (
                    <Reveal key={l.slug} delay={i * 60}>
                      {/* id = slug tetap dipasang: tautan rsjmc.id/karir#perawat
                          tetap berfungsi walau detailnya belum ada. */}
                      <article
                        id={l.slug}
                        className="flex h-full scroll-mt-32 flex-col gap-3 rounded-2xl border border-[var(--c-line)] bg-white p-5 dark:bg-white/5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Briefcase className="h-5 w-5 shrink-0 text-[#0f6a3b]" aria-hidden="true" />
                          <h3 className="text-lg font-bold tracking-tight text-[var(--c-ink)]">
                            {ambilTeks(l.posisi, kode)}
                          </h3>
                        </div>
                        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#0f6a3b]/20 bg-[#0f6a3b]/[0.06] px-3 py-1 text-xs font-medium text-[#0f6a3b]">
                          <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                          {ambilTeks({ id: "Sedang Disiapkan", en: "Being prepared" }, kode)}
                        </span>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
